import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Paper, Snackbar, Stack, Typography, Box } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import { AnimatePresence, motion } from 'framer-motion';

const InstallPwaPrompt = () => {
  const detectStandalone = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator?.standalone;
  }, []);

  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() => detectStandalone());
  const [isBannerVisible, setIsBannerVisible] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 900px)').matches;
  });

  const isIos = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  }, []);

  useEffect(() => {
    const handleBeforeInstall = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
      setBannerDismissed(false);
      if (!isMobile) {
        setIsBannerVisible(true);
      }
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsBannerVisible(false);
      setIsStandalone(true);
    };

    const refreshStandalone = () => {
      setIsStandalone(detectStandalone());
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('load', refreshStandalone);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('load', refreshStandalone);
    };
  }, [detectStandalone, isMobile]);

  useEffect(() => {
    if (!isStandalone && isIos && !bannerDismissed && !isMobile) {
      setIsBannerVisible(true);
    }
  }, [isIos, isStandalone, bannerDismissed, isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const media = window.matchMedia('(max-width: 900px)');
    const handler = (event) => setIsMobile(event.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (isMobile) {
      setIsBannerVisible(false);
    }
  }, [isMobile]);

  const installAvailable = !isStandalone && (!!deferredPrompt || isIos);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('zetflix-install-availability', {
      detail: { available: installAvailable }
    }));
  }, [installAvailable]);

  useEffect(() => {
    const openFromHeader = () => {
      if (!installAvailable) return;
      if (isMobile) {
        setShowInstructions(true);
        return;
      }
      setBannerDismissed(false);
      if (isIos && !deferredPrompt) {
        setShowInstructions(true);
      }
      setIsBannerVisible(true);
    };

    window.__zetflixOpenInstallPrompt = openFromHeader;

    return () => {
      delete window.__zetflixOpenInstallPrompt;
    };
  }, [installAvailable, isIos, deferredPrompt, isMobile]);

  const handleInstallClick = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsBannerVisible(false);
      }
    } else if (isIos) {
      setShowInstructions(true);
    }
  }, [deferredPrompt, isIos]);

  const handleCloseBanner = useCallback(() => {
    setBannerDismissed(true);
    setIsBannerVisible(false);
  }, []);

  if (!installAvailable && !isBannerVisible) return null;

  return (
    <>
      <AnimatePresence>
        {isBannerVisible && !isMobile && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 60, scale: 0.94 }}
            transition={{ duration: 0.28, ease: 'easeOut' }}
            sx={{
              position: 'fixed',
              bottom: { md: 220, lg: 240 },
              right: { md: 24, lg: 32 },
              zIndex: 1300
            }}
          >
            <Paper
              elevation={8}
              sx={{
                p: 2,
                borderRadius: 3,
                maxWidth: 240,
                backdropFilter: 'blur(18px)',
                backgroundColor: 'rgba(5,5,5,0.72)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: '0 22px 40px rgba(0,0,0,0.45)',
                color: 'common.white',
                transformOrigin: 'bottom right'
              }}
            >
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0.4 }}>
                  Install ZETFLIX App
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Keep ZETFLIX close. Install the app for a faster, full-screen experience.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button
                    onClick={handleCloseBanner}
                    color="inherit"
                    size="small"
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: '0.72rem',
                      letterSpacing: 0.6,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 2
                    }}
                  >
                    Maybe Later
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<GetAppIcon sx={{ fontSize: '1rem' }} />}
                    onClick={handleInstallClick}
                    sx={{
                      whiteSpace: 'nowrap',
                      fontSize: '0.72rem',
                      letterSpacing: 0.6,
                      px: 1.6,
                      py: 0.55,
                      borderRadius: 2,
                      boxShadow: '0 12px 22px rgba(255,31,31,0.35)',
                      '&:hover': {
                        boxShadow: '0 14px 24px rgba(255,31,31,0.45)'
                      }
                    }}
                  >
                    Install App
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Box>
        )}
      </AnimatePresence>

      <Snackbar
        open={showInstructions}
        autoHideDuration={8000}
        onClose={() => setShowInstructions(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          onClose={() => setShowInstructions(false)}
          variant="filled"
          sx={{ maxWidth: 420 }}
        >
          Tap the share icon in Safari and choose <strong>Add to Home Screen</strong> to install ZETFLIX on your device.
        </Alert>
      </Snackbar>
    </>
  );
};

export default InstallPwaPrompt;
